import { useMemo } from 'react'

import { useActiveOrgId } from '~/hooks/org'
import { useServerRequests } from '~/hooks/useRequests'
import { AddressSearchResult } from '~/types'
import { EvmAddress } from '~/utils/address'
import { createSingletonBatcher } from '~/utils/requests'

type ContactResult = Pick<
  AddressSearchResult,
  'address' | 'orgLabel' | 'smartAccountName'
>

type ResultByAddress = Record<string, ContactResult | undefined>
type PostFn = ReturnType<typeof useServerRequests>['serverPostRequest']

// Batches addresses for 100ms and makes one request to the contacts lookup
const getBatchContactLookup = (serverPostRequest: PostFn, orgId: string) => {
  return createSingletonBatcher<
    { address: string; orgId: string },
    string,
    ContactResult | undefined,
    ContactResult
  >(`contact-${orgId}`, {
    windowMs: 100,
    toKey: ({ address }) => EvmAddress.format(address),
    request: async (addresses) => {
      const result = await serverPostRequest<{ results: ResultByAddress }>({
        path: '/api/org/contacts/v2/lookup-by-addresses',
        params: {
          addresses,
          orgId,
        },
        // This is actually a fetch, it uses a POST to support large list of addresses,
        // so we don't need an error message
        errorMessage: '',
      })
      return result?.data?.results ?? {}
    },
    select: (contactData) => contactData,
  })
}

// Hook that provides a memoized batch lookup function
export const useBatchContactLookup = () => {
  const { serverPostRequest } = useServerRequests()
  const activeOrgId = useActiveOrgId()

  return useMemo(
    () =>
      activeOrgId
        ? getBatchContactLookup(serverPostRequest, activeOrgId)
        : null,
    [serverPostRequest, activeOrgId],
  )
}
